import { useState, useCallback } from 'react';
import { useSavedEvents } from '../../hooks';
import { useUserAuthOptional } from '../../contexts/UserAuthContext';
import type { Event } from '@buzzmap/shared';
import styles from './EventActions.module.css';
import { posthog } from '../../lib/analytics/posthogClient';

interface EventActionsProps {
  event: Event;
  /**
   * Layout variant.
   * - 'fixed' (default): floating fixed-bottom bar (mobile / EventPage).
   * - 'inline': static row inside the document flow (used by EventModal,
   *   so the action buttons live inside the modal container).
   */
  variant?: 'fixed' | 'inline';
}

/**
 * EventActions component with save/share buttons.
 * Default rendering is a floating fixed-bottom bar. Pass `variant="inline"`
 * to render the buttons as a static row (no fixed positioning) — used
 * inside EventModal on desktop where a fixed bar would escape the modal.
 */
export function EventActions({ event, variant = 'fixed' }: EventActionsProps) {
  const { isSaved, toggleSaved } = useSavedEvents();
  const { isLoading: isAuthLoading } = useUserAuthOptional();
  const [shareStatus, setShareStatus] = useState<'idle' | 'copied' | 'error'>('idle');

  const saved = isSaved(event.id);

  const handleSave = useCallback(() => {
    const willBeSaved = !saved;
    toggleSaved(event.id);
    posthog.capture('event_saved', {
      event_id: event.id,
      event_title: event.title,
      category: event.category,
      city: event.location.city,
      action: willBeSaved ? 'save' : 'unsave',
    });
  }, [event.id, event.title, event.category, event.location.city, saved, toggleSaved]);

  const handleShare = useCallback(async () => {
    const shareUrl = window.location.href;
    const shareData = {
      title: event.title,
      text: `${event.title} - ${event.location.city}`,
      url: shareUrl,
    };

    try {
      // Try native Web Share API first (mobile/PWA)
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        posthog.capture('event_shared', {
          event_id: event.id,
          event_title: event.title,
          category: event.category,
          city: event.location.city,
          method: 'native',
        });
        return;
      }

      // Fallback: copy URL to clipboard
      await navigator.clipboard.writeText(shareUrl);
      posthog.capture('event_shared', {
        event_id: event.id,
        event_title: event.title,
        category: event.category,
        city: event.location.city,
        method: 'clipboard',
      });
      setShareStatus('copied');

      // Reset status after 2 seconds
      setTimeout(() => {
        setShareStatus('idle');
      }, 2000);
    } catch (err) {
      // User cancelled share or copy failed
      if (err instanceof Error && err.name !== 'AbortError') {
        setShareStatus('error');
        setTimeout(() => {
          setShareStatus('idle');
        }, 2000);
      }
    }
  }, [event.title, event.location.city]);

  const getShareButtonText = () => {
    switch (shareStatus) {
      case 'copied':
        return 'Link copiato!';
      case 'error':
        return 'Errore';
      default:
        return 'Condividi';
    }
  };

  const containerClass = variant === 'inline' ? styles.containerInline : styles.container;

  return (
    <div className={containerClass}>
      <div className={styles.actions}>
        <button
          className={`${styles.actionButton} ${styles.shareButton} ${
            shareStatus === 'copied' ? styles.success : ''
          } ${shareStatus === 'error' ? styles.error : ''}`}
          onClick={handleShare}
          aria-label="Condividi evento"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {shareStatus === 'copied' ? (
              <polyline points="20 6 9 17 4 12" />
            ) : (
              <>
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </>
            )}
          </svg>
          <span>{getShareButtonText()}</span>
        </button>

        <button
          className={`${styles.actionButton} ${styles.saveButton} ${
            saved ? styles.saved : ''
          } ${isAuthLoading ? styles.disabled : ''}`}
          onClick={handleSave}
          disabled={isAuthLoading}
          aria-busy={isAuthLoading}
          aria-label={saved ? 'Rimuovi dai salvati' : 'Salva evento'}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill={saved ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
          <span>{saved ? 'Salvato' : 'Salva'}</span>
        </button>
      </div>
    </div>
  );
}
